// =============================================================================
// Settings Page - Claude CLI Configuration
// =============================================================================

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Settings,
  Key,
  Zap,
  TestTube,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Info,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  Cpu,
  Globe,
  Clock,
  RotateCcw
} from 'lucide-react';

const SettingsPage = () => {
  const { section } = useParams();
  const { token, isAuthenticated } = useAuth();
  const [activeSection, setActiveSection] = useState(section || 'claude');
  const [config, setConfig] = useState({
    apiKey: '',
    apiUrl: 'https://api.siliconflow.cn/',
    model: 'moonshotai/Kimi-K2-Instruct',
    maxTokens: 4096,
    temperature: 0.7,
    timeout: 30000,
    retryAttempts: 3,
    preferences: {
      autoSave: true,
      showTokenCount: true,
      enableHistory: true,
      maxHistorySize: 100
    }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [models, setModels] = useState([]);
  const [templates, setTemplates] = useState([]);

  // Load configuration on mount
  useEffect(() => {
    if (token) {
      loadConfig();
      loadModels();
      loadTemplates();
    }
  }, [token]);

  const loadConfig = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const response = await fetch('/api/claude-code/config', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data.data);
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadModels = async () => {
    if (!token) return;

    try {
      const response = await fetch('/api/claude-code/models', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setModels(data.data);
      }
    } catch (error) {
      console.error('Failed to load models:', error);
    }
  };

  const loadTemplates = async () => {
    if (!token) return;

    try {
      const response = await fetch('/api/claude-code/templates', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.data);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handleConfigChange = (key, value) => {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      setConfig(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        [key]: value
      }));
    }

    // Clear error for this field
    if (errors[key]) {
      setErrors(prev => ({
        ...prev,
        [key]: null
      }));
    }
  };

  const validateConfig = () => {
    const newErrors = {};

    if (!config.apiKey || config.apiKey.length < 10) {
      newErrors.apiKey = 'API Key is required and must be at least 10 characters';
    }

    if (!config.apiUrl || !config.apiUrl.startsWith('http')) {
      newErrors.apiUrl = 'Valid API URL is required';
    }

    if (!config.model) {
      newErrors.model = 'Model selection is required';
    }

    if (config.maxTokens < 100 || config.maxTokens > 200000) {
      newErrors.maxTokens = 'Max tokens must be between 100 and 200,000';
    }

    if (config.temperature < 0 || config.temperature > 2) {
      newErrors.temperature = 'Temperature must be between 0 and 2';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateConfig() || !token) {
      return;
    }

    try {
      setSaving(true);
      const response = await fetch('/api/claude-code/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        setTestResult({
          success: true,
          message: 'Configuration saved successfully!'
        });
      } else {
        const error = await response.json();
        setTestResult({
          success: false,
          message: error.error || 'Failed to save configuration'
        });
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Network error: ' + error.message
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!token) return;

    try {
      setTesting(true);
      setTestResult(null);

      const response = await fetch('/api/claude-code/config/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          testConfig: config
        })
      });

      const data = await response.json();
      setTestResult(data.data);
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Test failed: ' + error.message
      });
    } finally {
      setTesting(false);
    }
  };

  const applyTemplate = async (templateId) => {
    if (!token) return;

    try {
      const response = await fetch(`/api/claude-code/templates/${templateId}/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await loadConfig(); // Reload configuration
        setTestResult({
          success: true,
          message: 'Template applied successfully!'
        });
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Failed to apply template: ' + error.message
      });
    }
  };

  // Show authentication required message
  if (!isAuthenticated) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-yellow-800 mb-2">
            Authentication Required
          </h2>
          <p className="text-yellow-700 mb-4">
            You need to be logged in to access the settings page.
          </p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin" />
        <span className="ml-2">Loading configuration...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Configure your AI Development Workspace preferences and Claude CLI settings.
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'claude', name: 'Claude CLI', icon: Cpu },
            { id: 'workspace', name: 'Workspace', icon: Settings },
            { id: 'account', name: 'Account', icon: Key }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeSection === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Claude CLI Configuration */}
      {activeSection === 'claude' && (
        <div className="space-y-8">
          {/* Status Alert */}
          {testResult && (
            <div className={`p-4 rounded-lg border ${
              testResult.success
                ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200'
                : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
            }`}>
              <div className="flex items-center">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 mr-2" />
                ) : (
                  <AlertCircle className="w-5 h-5 mr-2" />
                )}
                <span>{testResult.message}</span>
              </div>
            </div>
          )}

          {/* Quick Templates */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Quick Setup Templates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                >
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    {template.name}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {template.description}
                  </p>
                  <button
                    onClick={() => applyTemplate(template.id)}
                    className="w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                  >
                    Apply Template
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* API Configuration */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center mb-4">
              <Key className="w-5 h-5 text-blue-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                API Configuration
              </h3>
            </div>

            <div className="space-y-4">
              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Key
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={config.apiKey}
                    onChange={(e) => handleConfigChange('apiKey', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md pr-10 dark:bg-gray-700 dark:text-white ${
                      errors.apiKey
                        ? 'border-red-300 dark:border-red-600'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="sk-ant-... or your API key"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.apiKey && (
                  <p className="text-red-500 text-sm mt-1">{errors.apiKey}</p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Your API key is encrypted and stored securely. Default uses SiliconFlow API.
                </p>
              </div>

              {/* API URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Base URL
                </label>
                <input
                  type="url"
                  value={config.apiUrl}
                  onChange={(e) => handleConfigChange('apiUrl', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white ${
                    errors.apiUrl
                      ? 'border-red-300 dark:border-red-600'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="https://api.siliconflow.cn/"
                />
                {errors.apiUrl && (
                  <p className="text-red-500 text-sm mt-1">{errors.apiUrl}</p>
                )}
              </div>

              {/* Model Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Model
                </label>
                <input
                  type="text"
                  value={config.model}
                  onChange={(e) => handleConfigChange('model', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white ${
                    errors.model
                      ? 'border-red-300 dark:border-red-600'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="moonshotai/Kimi-K2-Instruct"
                />
                {errors.model && (
                  <p className="text-red-500 text-sm mt-1">{errors.model}</p>
                )}
                <div className="mt-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Popular models:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      'moonshotai/Kimi-K2-Instruct',
                      'deepseek-ai/DeepSeek-V3',
                      'Qwen/Qwen2.5-72B-Instruct',
                      'claude-3-5-sonnet-20241022',
                      'claude-3-5-haiku-20241022'
                    ].map((modelName) => (
                      <button
                        key={modelName}
                        type="button"
                        onClick={() => handleConfigChange('model', modelName)}
                        className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        {modelName}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Model Parameters */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center mb-4">
              <Zap className="w-5 h-5 text-yellow-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Model Parameters
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Max Tokens */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Tokens
                </label>
                <input
                  type="number"
                  min="100"
                  max="200000"
                  value={config.maxTokens}
                  onChange={(e) => handleConfigChange('maxTokens', parseInt(e.target.value))}
                  className={`w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:text-white ${
                    errors.maxTokens
                      ? 'border-red-300 dark:border-red-600'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.maxTokens && (
                  <p className="text-red-500 text-sm mt-1">{errors.maxTokens}</p>
                )}
              </div>

              {/* Temperature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Temperature ({config.temperature})
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={config.temperature}
                  onChange={(e) => handleConfigChange('temperature', parseFloat(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>Focused (0)</span>
                  <span>Balanced (1)</span>
                  <span>Creative (2)</span>
                </div>
              </div>

              {/* Timeout */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Timeout (seconds)
                </label>
                <input
                  type="number"
                  min="5"
                  max="300"
                  value={config.timeout / 1000}
                  onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value) * 1000)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Retry Attempts */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Retry Attempts
                </label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={config.retryAttempts}
                  onChange={(e) => handleConfigChange('retryAttempts', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleTest}
              disabled={testing}
              className="flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              {testing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <TestTube className="w-4 h-4 mr-2" />
              )}
              Test Connection
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Configuration
            </button>
          </div>
        </div>
      )}

      {/* Other sections placeholder */}
      {activeSection !== 'claude' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-center py-12">
            <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {activeSection === 'workspace' ? 'Workspace Settings' : 'Account Settings'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              This section is coming soon. Configure your {activeSection} preferences here.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
